"use client";

import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import AppWrapper from "@/shared/ui/components/app-wrapper";
import Button from "@/shared/ui/components/button";
import { type CheckoutSession } from "@/types";
import { useCheckoutSessionV3 } from "@/modules/checkout-session";
import { logInfo, logError } from "@/services/logging/logger";
import CircularProgress from "@mui/material/CircularProgress";
import { browserAgent } from "@/shared/telemetry/newrelic";
import { newLocalStorageStore } from "@/lib/store";
import { withCheckoutId } from "@/lib/checkout-id";
import { type ExternalVerificationConfig } from "../types";
import * as externalVerificationLib from "../lib";
import { VerificationIcon } from "../components/verification-icon";
import { syncCheckoutHandoffCookies } from "@/services/cookie/checkout-handoff";
import { parseLocale } from "@/i18n";

interface ExternalVerificationRedirectPageProps {
  config: ExternalVerificationConfig;
  onCheckoutFetched?: (session: CheckoutSession) => void;
}

const COUNTDOWN_SECONDS = 3;

export function ExternalVerificationRedirectPage({
  config,
}: ExternalVerificationRedirectPageProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { data: session } = useCheckoutSessionV3();
  const [countdown, setCountdown] = useState(COUNTDOWN_SECONDS);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const hasRedirectedRef = useRef(false);
  const languageRef = useRef(i18n.language);

  useEffect(() => {
    languageRef.current = i18n.language;
  }, [i18n.language]);

  const localePrefix = config.localeKey;
  const sessionId = session?.id ?? "";

  const redirectUrlFromSession = useMemo(() => {
    if (!sessionId) return "";
    try {
      const storedUrl = sessionStorage.getItem(
        externalVerificationLib.redirectSessionKey(config, sessionId),
      );
      if (storedUrl) return storedUrl;
    } catch {
      // sessionStorage might be blocked
    }
    return session?.redirectUrl ?? "";
  }, [session?.redirectUrl, sessionId, config]);

  const performRedirect = useCallback(async () => {
    if (hasRedirectedRef.current || isRedirecting) {
      return;
    }

    const store = newLocalStorageStore(window, sessionId);
    const alreadyInitiated =
      await externalVerificationLib.isRedirectInitiated(store, config);
    if (alreadyInitiated) {
      logInfo(
        config.logTag,
        "External verification redirect already initiated, navigating to return handler",
        { sessionId },
      );
      navigate(withCheckoutId(config.returnPath), { replace: true });
      return;
    }

    if (!redirectUrlFromSession) {
      logError(
        config.logTag,
        "No external verification redirect URL available",
        new Error("Missing redirectUrl"),
        { sessionId },
      );
      navigate(withCheckoutId("/"), { replace: true });
      return;
    }

    const localizedRedirectUrl =
      externalVerificationLib.applyLocaleToExternalRedirectUrl(
        redirectUrlFromSession,
        languageRef.current,
      );

    hasRedirectedRef.current = true;
    setIsRedirecting(true);

    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }

    logInfo(config.logTag, "Redirecting to external verification UI", {
      sessionId,
      redirectUrl: localizedRedirectUrl.substring(0, 50) + "...",
    });

    await externalVerificationLib.setRedirectInitiated(store, config);

    browserAgent.addPageAction(config.telemetry.redirectToUi, {
      checkout_id: sessionId,
      redirect_url_domain: (() => {
        try {
          return new URL(localizedRedirectUrl).hostname;
        } catch {
          return "unknown";
        }
      })(),
    });

    try {
      sessionStorage.removeItem(
        externalVerificationLib.redirectSessionKey(config, sessionId),
      );
    } catch {
      // Ignore
    }

    syncCheckoutHandoffCookies({
      customerId: session?.customerId,
      locale: parseLocale(languageRef.current) ?? session?.locale,
    });

    window.location.href = localizedRedirectUrl;
  }, [
    redirectUrlFromSession,
    sessionId,
    session?.customerId,
    session?.locale,
    isRedirecting,
    navigate,
    config,
  ]);

  const handleManualRedirect = useCallback(() => {
    performRedirect();
  }, [performRedirect]);

  useEffect(() => {
    if (!session || !sessionId || !redirectUrlFromSession) return;

    try {
      sessionStorage.setItem(
        externalVerificationLib.redirectSessionKey(config, sessionId),
        redirectUrlFromSession,
      );
    } catch {
      // Ignore
    }
  }, [session, sessionId, redirectUrlFromSession, config]);

  useEffect(() => {
    if (!session) return;
    if (redirectUrlFromSession) return;

    const timeoutId = setTimeout(() => {
      logInfo(
        config.logTag,
        "No redirect URL after waiting, navigating home",
        { sessionId, status: session.status },
      );
      navigate(withCheckoutId("/"), { replace: true });
    }, 2000);

    return () => clearTimeout(timeoutId);
  }, [session, redirectUrlFromSession, sessionId, navigate, config]);

  useEffect(() => {
    if (isRedirecting || !redirectUrlFromSession || !session) {
      return;
    }

    countdownIntervalRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
          }
          performRedirect();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    };
  }, [isRedirecting, redirectUrlFromSession, session, performRedirect]);

  if (!session) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <CircularProgress size={40} style={{ color: "#9600F1" }} />
      </div>
    );
  }

  return (
    <AppWrapper
      visible={true}
      showLogo
      showCartButton={false}
      showLanguageSwitcher
      headerBottomSpacing="none"
      stickyFooterWithShadow
      stickyFooter={() => (
        <Button
          size="large"
          onClick={handleManualRedirect}
          loading={isRedirecting}
          disabled={isRedirecting || !redirectUrlFromSession}
        >
          {isRedirecting
            ? t(`${localePrefix}.redirecting`)
            : t(`${localePrefix}.proceed`)}
        </Button>
      )}
    >
      {() => (
        <div className="flex flex-col items-center justify-center min-h-[60vh] px-6 py-10">
          <div className="w-24 h-24 mb-8 rounded-full bg-gradient-to-br from-purple-100 to-purple-200 flex items-center justify-center text-purple-600">
            <VerificationIcon icon={config.redirectIcon} />
          </div>

          <h1 className="text-2xl font-bold text-gray-900 text-center mb-3">
            {t(`${localePrefix}.title`)}
          </h1>

          <p className="text-gray-600 text-center text-lg mb-8 max-w-xs">
            {t(`${localePrefix}.redirecting_message`)}
          </p>

          {!isRedirecting && countdown > 0 && redirectUrlFromSession && (
            <div className="flex flex-col items-center mb-6">
              <div className="relative w-20 h-20 mb-4">
                <svg
                  className="w-20 h-20 transform -rotate-90"
                  viewBox="0 0 36 36"
                  aria-hidden="true"
                >
                  <path
                    className="text-gray-200"
                    d="M18 2.0845
                        a 15.9155 15.9155 0 0 1 0 31.831
                        a 15.9155 15.9155 0 0 1 0 -31.831"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                  />
                  <path
                    className="text-purple-600 transition-all duration-1000 ease-linear"
                    d="M18 2.0845
                        a 15.9155 15.9155 0 0 1 0 31.831
                        a 15.9155 15.9155 0 0 1 0 -31.831"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeDasharray={`${(countdown / COUNTDOWN_SECONDS) * 100}, 100`}
                  />
                </svg>
                <span className="absolute inset-0 flex items-center justify-center text-2xl font-bold text-purple-600">
                  {countdown}
                </span>
              </div>
              <p className="text-sm text-gray-500">
                {countdown === 1
                  ? t(`${localePrefix}.auto_redirect`, { seconds: countdown })
                  : t(`${localePrefix}.auto_redirect_plural`, {
                      seconds: countdown,
                    })}
              </p>
            </div>
          )}

          {isRedirecting && (
            <div className="flex flex-col items-center">
              <CircularProgress size={32} style={{ color: "#9600F1" }} />
              <p className="text-sm text-gray-500 mt-4">
                {t(`${localePrefix}.taking_you`)}
              </p>
            </div>
          )}

          <div className="flex items-center gap-2 mt-8 px-4 py-2 bg-purple-50 rounded-full">
            <svg
              className="w-4 h-4 text-purple-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span className="text-sm text-purple-700 font-medium">
              {t(`${localePrefix}.time_badge`)}
            </span>
          </div>

          <p className="text-xs text-gray-400 text-center mt-6 max-w-xs">
            {t(`${localePrefix}.info_hint`)}
          </p>
        </div>
      )}
    </AppWrapper>
  );
}
